// @ts-nocheck
import { forwardRef, useRef } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Menu,
  ActionIcon,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { Card } from '../../../ui';
import { FadeIn } from '../../../animations';
import { exportChart } from './utils/exportChart';
import type { PieChartWidgetProps } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

const PieChartWidget = forwardRef<HTMLDivElement, PieChartWidgetProps>(
  (
    {
      title,
      subtitle,
      data,
      variant = 'pie',
      showPercentages = true,
      showLegend = true,
      showExport = true,
      onExport,
      centerLabel,
      height = 300,
      innerRadius = 0,
      outerRadius = 80,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const chartRef = useRef<HTMLDivElement>(null);
    const isDark = colorScheme === 'dark';
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const handleExport = async (format: 'png' | 'svg' | 'csv') => {
      if (onExport) {
        onExport({ format, filename: `${title?.toLowerCase().replace(/\s+/g, '-')}-chart` });
      } else if (chartRef.current) {
        await exportChart(chartRef.current, data, {
          format,
          filename: `${title?.toLowerCase().replace(/\s+/g, '-')}-chart`,
        });
      }
    };

    const total = data.reduce((sum, item) => sum + item.value, 0);

    const renderLabel = (entry: any) => {
      if (!showPercentages) return null;
      const percent = ((entry.value / total) * 100).toFixed(1);
      return `${percent}%`;
    };

    const actualInnerRadius = variant === 'donut' ? innerRadius || 60 : 0;

    return (
      <FadeIn>
        <Card ref={ref} withBorder p="lg" className={className}>
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <Box>
                {title && <Text size="lg" fw={600} style={accessibilityStyles}>{title}</Text>}
                {subtitle && <Text size="sm" c="dimmed" style={accessibilityStyles}>{subtitle}</Text>}
              </Box>

              {showExport && (
                <Menu shadow="md" width={150}>
                  <Menu.Target>
                    <ActionIcon variant="light" size="lg">
                      <IconDownload size={18} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>Export</Menu.Label>
                    <Menu.Item onClick={() => handleExport('png')}>Export as PNG</Menu.Item>
                    <Menu.Item onClick={() => handleExport('svg')}>Export as SVG</Menu.Item>
                    <Menu.Item onClick={() => handleExport('csv')}>Export as CSV</Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>

            <Box ref={chartRef} style={{ width: '100%', height, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderLabel}
                    outerRadius={outerRadius}
                    innerRadius={actualInnerRadius}
                    fill="#8884d8"
                    dataKey="value"
                    animationDuration={1000}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color || theme.colors.blue[6]}
                      />
                    ))}
                  </Pie>

                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
                      border: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                      borderRadius: theme.radius.sm,
                    }}
                  />

                  {showLegend && <Legend />}
                </PieChart>
              </ResponsiveContainer>

              {centerLabel && variant === 'donut' && (
                <Box
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <Text size="xl" fw={700} style={accessibilityStyles}>{centerLabel}</Text>
                </Box>
              )}
            </Box>
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

PieChartWidget.displayName = 'PieChartWidget';

export default PieChartWidget;
